// The card kit (ui-map-interface.md §8 "The card family"; mockup round 083
// cards 06 to 09, round 084 card 18, round 087 card 03).
//
// One object has one card in three sizes. The PEEK is the head, the vitals
// and at most two facts, with at most one action: it stands beside the
// object on a hover or a long press. The CARD is the selection, with
// every part. The PAGE is the card at the full width of its host. One
// builder writes all three, so the peek and the card state one thing in
// the same words.
//
// The parts stand in one order, and a card leaves out a part it does not
// need: head, vitals, conds (the conditions row), facts, body, effect,
// note, the ⋯ grid, actions. A peek draws its own body, `peekBody`, in the
// place of the body: the terrain of a ridge, the ramp of a heat cell.
//
// The module is pure: it turns a `CardSpec` into markup and reads no
// state. The caller escapes nothing it passes as plain text (`title`,
// `kind`, a label); a field named `...Html` or `val` is trusted markup the
// caller built from escaped parts.

import type { GuardPhase } from '../../render/guard-look';
import { card as S } from '../../gen/strings';
import { editGlyph } from '../arrowpress';
import { armFaces } from '../choice/choice';
import { esc } from '../esc';
import { factionColor, factionName, hex } from '../../world';
import { condsHtml, type Cond } from './conds';
import { clamp01 } from '../../render/math';

/** A faction's colour as a CSS hex: `ui/format.ts` `cssColor`, written here
 *  from `world.ts`, so a page with no game state (a lab, lab-shell.md §5)
 *  takes the card kit without the state module that `format.ts` imports. */
const cssColor = (faction: number): string => hex(factionColor(faction));

export type { Cond };

/** The three sizes of one card. */
export type CardSize = 'peek' | 'card' | 'page';

/** The tone of a chip: a verdict ink, the accent ("on"), a solid count, a
 *  dashed kind chip, or the armed order ("pick", the accent ground, R2164).
 *  The empty tone is the plain chip. */
export type ChipTone = '' | 'good' | 'bad' | 'amber' | 'on' | 'solid' | 'kind' | 'pick';

/**
 * The armed order of a card (R2164): the line that says what the next press
 * on the map does, in the text ink, and the CANCEL press. It stands under
 * the head of the card and under the line of a closed context sheet, so the
 * reader sees the armed state where the card stands.
 */
export interface ArmLine {
  line: string;
  cancel: Action;
}

/** What an armed order says (R2164): the order word of the chip, the line
 *  of the next map press, and `aim` for a shot, whose chip reads AIM. */
export interface ArmWords {
  order: string;
  line: string;
  aim: boolean;
}

/**
 * The armed state of a card (R2164): the order chip reads the order and
 * PICK (or AIM) in the tone `pick` on the kind line, and the armed line
 * says what the next map press does, with CANCEL (`cancelmodes`), whose
 * key cap is `escKey`. Null while nothing is armed. The season card and
 * the arcade card both take it.
 */
export function armState(a: ArmWords | null, escKey: string): { status: { text: string; tone: ChipTone }; armed: ArmLine } | null {
  if (!a) return null;
  return {
    status: { text: a.aim ? S.armed_aim_chip({ order: a.order }) : S.armed_chip({ order: a.order }), tone: 'pick' },
    armed: { line: a.line, cancel: { act: 'cancelmodes', face: 'quiet', label: S.arm_cancel, key: escKey } },
  };
}

/**
 * Whose a thing is, which sets the ink of its title (§2.4). A map object
 * wears the faction colour of its owner; the side no player commands wears
 * the NPC ink (round 088 card 03 a, a v2 slot); a catalogue item wears the
 * hero ink; a thing that belongs to nobody (open water, a deposit, an
 * unclaimed object) wears the text ink.
 */
export type CardInk = { faction: number } | { npc: true } | 'catalogue' | 'neutral';

/** The bar ink of a vital. */
export type VitalInk = 'hull' | 'shield' | 'ready' | 'load' | 'store' | 'gauge';

/**
 * One vital: a cap label, a tabular figure and a 4 px bar (083 card 07).
 * An unknown value is the hatched bar with "?", never a missing vital. A
 * running wait is a short count after the label, and a load in progress a
 * lighter segment after the fill (R1853).
 */
export interface Vital {
  label: string;
  /** The figure as markup, for example `784<small>/960</small>`. */
  fig: string;
  /** The fill, 0 to 1. Ignored when `unknown`. */
  frac: number;
  unknown?: boolean;
  ink?: VitalInk;
  /** The bar turns `--bad`: a hull under the low band. */
  low?: boolean;
  /** A 1 px tick at this share: the readiness floor (R769). */
  tick?: number;
  /** The seconds of a running wait on the clock the card prints: the
   *  shield regen (R1503) or the tube reload. Over 0, the label row
   *  states it as a short count (`waitCount`, R1853). */
  wait?: number;
  /** The part of the bar that loads now, 0 to 1 of the whole bar: the
   *  next tube of a reload. It stands after the fill as a lighter
   *  segment (R1853). A wait with no progress figure sets none. */
  loading?: number;
  /** A solid diamond after the label: a talent of a captain (084 card
   *  16), a shape and not an ink. */
  talent?: boolean;
  /** The plain-text title of the vital: what the figure is worth. */
  title?: string;
  /** A value that goes stale: the fill, 1 to 0, over the hatched track
   *  of an unknown value. A scan reveal that runs out sets it (R1837). */
  fade?: number;
}

/**
 * The readout chip of a guard (R2080, mockup round 099 card 04): the pie of
 * its clock, its cap word, the count of hostile rounds inside its reach
 * while the clock runs (the blocked state, a bad condition), and its wait
 * as the short count of `waitCount`, or READY.
 */
export interface GuardChip {
  /** The cap word: what the host calls its kill. */
  label: string;
  /** The share of the clock left, 0 (ready) to 1. */
  frac: number;
  /** The wait in the seconds the card prints; 0 when ready. */
  wait: number;
  /** The hostile rounds in reach while the clock runs; 0 otherwise. */
  blocked: number;
  title?: string;
}

/** A guard's clock as a card reads it (R2080): the share left, the map
 *  seconds left, and what the map draws now. */
export interface GuardRead {
  frac: number;
  leftSec: number;
  phase: GuardPhase;
  near: number;
}

/** The readout chip of a guard (R2080, round 099 card 04). `toCard` puts
 *  the map seconds of the wire on the clock the card prints (R591). */
export function guardChip(label: string, g: GuardRead, toCard: (mapSec: number) => number): GuardChip {
  return {
    label, frac: g.frac, wait: g.leftSec > 0 ? toCard(g.leftSec) : 0,
    blocked: g.phase === 'blocked' ? g.near : 0, title: S.guard_title,
  };
}

/** One fact: a cap key at the left and a value at the right. */
export interface Fact {
  key: string;
  /** Trusted markup. */
  val: string;
  title?: string;
}

/** The HELD BY fact of a cell or a site: its faction, or unclaimed. */
export function heldByFact(faction: number): Fact {
  return { key: S.f_held_by, val: esc(faction === 0 ? S.unclaimed : factionName(faction)) };
}

/** One row of the effect block of a catalogue item: NOW → AFTER. */
export interface EffectRow {
  key: string;
  now: string;
  after: string;
  delta?: string;
  tone?: 'good' | 'bad';
}

/**
 * One press. The primary takes the dark face, a secondary the quiet face
 * (087 card 03); a card never draws a solid light press (R1753). The rust
 * solid face is the end card press of a drill (R1807).
 */
export interface Action {
  act: string;
  label: string;
  face?: 'dark' | 'quiet' | 'rust';
  id?: number;
  n?: number;
  /** A word the host reads back off the press as `data-key`: the arcade
   *  corner names its order chip with it (R836, the arcade order row). */
  dataKey?: string;
  /** A running order or an armed pick: the press is lit. */
  on?: boolean;
  /** The press cannot act now. */
  off?: boolean;
  /** The key cap a desktop shows at the right of the word. */
  key?: string;
  title?: string;
  /** Trusted markup after the word: the cost of a build press (the verb
   *  press of a build row on its card, R1826). */
  costHtml?: string;
  /** Trusted markup in place of the escaped `label`: a word with a
   *  resource icon, RELEASE ¤60 (R1889). */
  labelHtml?: string;
  /** The press destroys something and asks for a second press (R696):
   *  it carries `data-arm`, which the overlay's arm gate reads. */
  arm?: boolean;
  /** The armed face of an armed press, the word that names the act
   *  (BREAK UP?, SCRAP?, R2069). The press holds it and its rest face in
   *  one grid cell (`armFaces`), so it keeps its width when it arms. */
  armLabel?: string;
  /** The first press armed the press: it wears the `armed` look and
   *  shows `armLabel` (R696). */
  armed?: boolean;
  /** The press destroys something: it wears the bad ink (BREAK, SCRAP). */
  danger?: boolean;
}

/** One card. Every part but the head is optional. */
export interface CardSpec {
  size: CardSize;
  /** The 20 px mark or icon of the head, as markup (`marks.ts`). */
  mark?: string;
  title: string;
  /** Markup after the title: a group chip, a REPORT mark. */
  titleHtml?: string;
  ink: CardInk;
  /** The cap line under the title: what the thing is. */
  kind?: string;
  /** Trusted markup in place of `kind`: a kind line with a term button of
   *  the player glossary (R1557). */
  kindHtml?: string;
  status?: { text: string; tone?: ChipTone };
  /**
   * The chart position of the thing, `14°05′N 031°55′W` (R2742): the
   * figures the graticule labels, so a reader finds the place on the edge
   * of the chart. Every peek of the map states it on the line under the
   * kind line, in one form. Plain text; empty or absent draws no line.
   */
  pos?: string;
  /**
   * The status chip stands at the end of the kind line, not at the right
   * of the head, so a long chip never takes the width of the title. A
   * strategic object sets it: its chip WINDOW hh:00 is long, and its name
   * must read whole in the peek (R1879).
   */
  statusOnKind?: boolean;
  /**
   * The faction that owns the thing, when the title ink does not name it:
   * the owner of the cell a heat cell peek reads. 0 is nobody. It sets the
   * faction edge alone (R1875).
   */
  owner?: number;
  /** The ✕. A peek never draws one. */
  close?: boolean;
  /** The act of the ✕: `deselect` by default; the build card closes with
   *  `infoclose`. */
  closeAct?: string;
  /** Trusted markup at the end of the head, after the title: the ✕ of a raised
   *  context sheet (ui-map-interface.md §12.0), the presses of a drill
   *  step. */
  headExtra?: string;
  /** The armed order (R2164): its line and CANCEL. A peek draws none. */
  armed?: ArmLine;
  vitals?: Vital[];
  /** The readout chip of a guard (R2080): its clock as a pie, its wait
   *  and, while it is blocked, the hostile rounds in its reach. */
  guard?: GuardChip;
  conds?: Cond[];
  facts?: Fact[];
  /** Markup after the facts: a segmented pick, a list, a compare grid. */
  body?: string;
  /**
   * Markup after the facts that the PEEK keeps, where a card draws `body`:
   * the elevation of a ridge (round 086 card 06 b) or the ramp of a heat
   * cell (card 12). A peek draws no other body.
   */
  peekBody?: string;
  /**
   * Trusted markup that the CLOSED context sheet of a telephone draws
   * between the peek line (and the armed line) and the action row
   * (`ctxsheet.ts closedHtml`): the order row of an own hull's signature
   * order (R2415). The raised card and the card of a desktop never draw
   * it; they keep that section in `body`.
   */
  closedRow?: string;
  effect?: EffectRow[];
  /** One line in the small step, or a few. A `bad` note is a refusal.
   *  `foot` stands the note under the actions, so a long note never
   *  pushes the press down (the start sheet of a dive). */
  note?: { lines: string[]; tone?: 'bad'; foot?: boolean };
  actions?: Action[];
  /** The peek keeps two actions, not one: the work press of the treasure
   *  beside MARK on a pinned peek of water (v2 §9, R2629, R2649). */
  peekPair?: boolean;
  /** The ⋯ press and, while it is open, the grid of the rest: `items`
   *  under the head `head`, then each section under its own head (R1845).
   *  An own fleet's ⋯ holds the orders alone (R2008). */
  more?: { open: boolean; head?: string; items: Action[]; sections?: MoreSection[] };
  /** EDIT in the action row, left of ⋯, and while it is open the EDIT grid in
   *  the place of the ⋯ grid (R2008): the group slots and RENAME of an own
   *  fleet. `title` names the press; the two grids exclude each other. */
  edit?: { open: boolean; title: string; sections: MoreSection[] };
}

/** One section of the ⋯ grid or the EDIT grid: a head over a rule, then
 *  its presses on rows of three (R1845, R2008). */
export interface MoreSection {
  head: string;
  items: Action[];
  /** A choice of the section in the place of presses, as trusted markup
   *  of the choice kit: the group slots of the EDIT grid (R2011). It
   *  spans the width of the grid under the head. */
  pick?: string;
}

/** What the builder needs to know of the screen. */
export interface CardEnv {
  /** A desktop with a mouse shows the key caps; a finger does not. */
  keycaps: boolean;
}

/**
 * A peek with a head and the note lines and no facts: open water, a ridge,
 * a firing solution (R1810). Nobody owns it unless `faction` says so. The
 * lines are plain text. The season map and the arcade map both build it
 * (R2424).
 */
export function notePeek(title: string, lines: string[], faction = 0, gauge?: Vital): CardSpec {
  return {
    size: 'peek', title, ink: faction === 0 ? 'neutral' : { faction },
    vitals: gauge ? [gauge] : undefined,
    note: { lines: lines.map(esc) },
  };
}

/**
 * The gauge of a held cell in the vital idiom against the cap the catalog
 * states (084 open point 8): no dial (round 084 card 01). `v` runs from 0
 * to `max`; a cap of 0 prints the figure alone.
 */
export function gaugeVital(v: number, max: number): Vital {
  return {
    label: S.v_gauge, ink: 'gauge', frac: max > 0 ? v / max : 0,
    fig: max > 0 ? `${v.toFixed(1)}<small>/${max}</small>` : v.toFixed(1),
  };
}

/** The peek of a card: the head, the vitals and at most two facts, with at
 *  at most one action and no ✕ (083 card 06, 084 card 01). A pinned peek of
 *  water with the work press keeps two actions (`peekPair`, R2649). */
export function peekOf(spec: CardSpec): CardSpec {
  return {
    size: 'peek', mark: spec.mark, title: spec.title, titleHtml: spec.titleHtml,
    ink: spec.ink, owner: spec.owner, kind: spec.kind, pos: spec.pos, status: spec.status,
    statusOnKind: spec.statusOnKind, close: false,
    vitals: spec.vitals?.slice(0, 4),
    facts: spec.facts?.slice(0, 2),
    peekBody: spec.peekBody,
    note: spec.note && !spec.facts?.length ? { ...spec.note, lines: spec.note.lines.slice(0, 2) } : undefined,
    actions: spec.actions?.slice(0, spec.peekPair ? 2 : 1),
  };
}

/**
 * The faction edge (R1875): the left side of the border of a peek and a
 * card takes the ink of the owner of the thing it names, as the tooltip
 * did before the card family. A map object names its owner by its title
 * ink (§2.4); a spec may name the owner apart from the ink (`owner`). A
 * thing nobody owns, a catalogue item and a page keep the plain edge.
 * The class and the property go into the markup, so the markup guard of a
 * host redraws on a change of the owner and on nothing else.
 */
function edgeAttr(spec: CardSpec): string {
  if (spec.size === 'page') return '';
  if (spec.owner !== undefined) {
    return spec.owner > 0 ? ` style="--ck-own:${cssColor(spec.owner)}"` : '';
  }
  const ink = spec.ink;
  if (ink === 'catalogue' || ink === 'neutral') return '';
  if ('npc' in ink) return ' style="--ck-own:var(--npc)"';
  return ink.faction > 0 ? ` style="--ck-own:${cssColor(ink.faction)}"` : '';
}

/** The style or class of a title ink. The context sheet writes its peek
 *  line with it too (ui/ctxsheet.ts). */
export function inkAttr(ink: CardInk): string {
  if (ink === 'catalogue') return ' class="i-cat"';
  if (ink === 'neutral') return ' class="i-neu"';
  if ('npc' in ink) return ' class="i-npc"';
  return ` style="color:${cssColor(ink.faction)}"`;
}

/** One chip. */
export function chipHtml(text: string, tone: ChipTone = ''): string {
  return `<span class="ck-chip${tone ? ` ${tone}` : ''}">${esc(text)}</span>`;
}

/**
 * The group digit after the name of a fleet (R1845): the look of the group
 * badge of the map, the owner's faction colour on the dark ground with an
 * outline of the same ink. `faction` sets the ink, so a caller writes no
 * colour.
 */
export function groupChipHtml(n: number, faction: number): string {
  return `<span class="ck-grp" style="color:${cssColor(faction)}" title="${esc(S.group_title({ n }))}">${n}</span>`;
}

/** The one ✕: 24 px painted, 44 px to a finger (083 card 08). It closes
 *  the card and clears the selection. */
function closeHtml(act = 'deselect'): string {
  return `<button class="ck-x" data-act="${act}" title="${S.close_title}"`
    + ` aria-label="${S.close_title}">&#10005;</button>`;
}

/** The class of each face of a press; no face is the quiet face. */
const FACE_CLASS: Readonly<Record<NonNullable<Action['face']>, string>> = { dark: 'dk', quiet: 'qt', rust: 'rust' };

/** One press in the dark face (the primary), the quiet face or the rust face. */
export function buttonHtml(a: Action, env: CardEnv): string {
  const cls = ['ck-b', FACE_CLASS[a.face ?? 'quiet']];
  if (a.on) cls.push('on');
  if (a.danger) cls.push('dg');
  if (a.armed) cls.push('armed');
  const word = a.labelHtml ?? esc(a.label);
  return `<button class="${cls.join(' ')}" data-act="${a.act}"`
    + (a.id !== undefined ? ` data-id="${a.id}"` : '')
    + (a.n !== undefined ? ` data-n="${a.n}"` : '')
    + (a.dataKey !== undefined ? ` data-key="${esc(a.dataKey)}"` : '')
    + (a.arm ? ' data-arm="1"' : '')
    + (a.off ? ' disabled' : '')
    + (a.title ? ` title="${esc(a.title)}"` : '')
    + `><span>${a.armLabel === undefined ? word : armFaces(word, esc(a.armLabel), a.armed === true)}</span>`
    + (a.costHtml ? `<span class="ck-bc">${a.costHtml}</span>` : '')
    + (env.keycaps && a.key ? `<kbd class="ck-key">${esc(a.key)}</kbd>` : '')
    + '</button>';
}

/** The armed order line (R2164): the words of the next map press and the
 *  CANCEL press. The overlay's hint host draws it too, in a card of its
 *  own, where no card names the armed hull. */
export function armHtml(arm: ArmLine | undefined, env: CardEnv): string {
  if (!arm) return '';
  return `<div class="ck-arm"><p>${esc(arm.line)}</p>${buttonHtml(arm.cancel, env)}</div>`;
}

/**
 * The short count of a wait (R1853): whole seconds under 100 s (`↻12s`),
 * else minutes rounded to the nearest whole minute, never floored
 * (100 s is `↻2m`, 209 s is `↻3m`). The seconds round up first, so the
 * last second reads `↻1s`. It never takes more than four characters
 * under 100 minutes, so the label row keeps one fixed slot.
 */
export function waitCount(sec: number): string {
  const s = Math.ceil(sec);
  return s < 100 ? S.wait_sec({ n: s }) : S.wait_min({ n: Math.round(s / 60) });
}

/**
 * The part of the TUBES bar that loads now (R1853): the share of the next
 * tube, filled as far as its reload has run (`reload_sec` against
 * `reload_full_sec`). A full magazine, an unknown magazine or no full
 * reload figure gives 0, so the vital states the count alone.
 */
export function tubeLoading(loaded: number, magazine: number, leftSec: number, fullSec: number): number {
  if (!(magazine > 0) || loaded >= magazine || !(fullSec > 0) || !(leftSec > 0)) return 0;
  const done = clamp01(1 - leftSec / fullSec);
  return done / magazine;
}

/** One vital. */
export function vitalHtml(v: Vital): string {
  const frac = v.unknown ? 0 : clamp01(v.frac);
  const bar = ['ck-bar', `i-${v.ink ?? 'hull'}`];
  const fade = !v.unknown && v.fade !== undefined && v.fade < 1 ? Math.max(0, v.fade) : null;
  if (v.unknown || fade !== null) bar.push('unk');
  if (v.low && !v.unknown) bar.push('low');
  const wait = !v.unknown && v.wait !== undefined && v.wait > 0 ? waitCount(v.wait) : '';
  const loading = !v.unknown && fade === null && v.loading !== undefined && v.loading > 0
    ? Math.min(v.loading, 1 - frac) : 0;
  return `<div class="ck-vt"${v.title ? ` title="${esc(v.title)}"` : ''}>`
    + '<span class="ck-vl">' + (wait ? '<span class="ck-vh">' : '')
    + `<span class="ck-cap">${esc(v.label)}`
    + (v.talent ? `<i class="ck-tal" title="${S.talent_title}"></i>` : '') + '</span>'
    + (wait ? `<i class="ck-vw">${esc(wait)}</i></span>` : '')
    + `<b class="ck-vf">${v.unknown ? S.unknown : v.fig}</b></span>`
    + `<i class="${bar.join(' ')}"><b style="width:${(frac * 100).toFixed(1)}%`
    + (fade !== null ? `;opacity:${fade.toFixed(2)}` : '') + '"></b>'
    + (v.tick !== undefined && v.tick > 0 && !v.unknown
      ? `<u style="left:${(clamp01(v.tick) * 100).toFixed(1)}%"></u>` : '')
    + (loading > 0
      ? `<em style="left:${(frac * 100).toFixed(1)}%;width:${(loading * 100).toFixed(1)}%"></em>` : '')
    + '</i></div>';
}

/** The readout chip of a guard (R2080). The pie fills clockwise as the
 *  clock runs out: `--d` is the share done. */
export function guardChipHtml(g: GuardChip | undefined): string {
  if (!g) return '';
  const done = clamp01(1 - g.frac);
  return `<div class="ck-guard"${g.title ? ` title="${esc(g.title)}"` : ''}>`
    + `<span class="ck-pie${g.frac <= 0 ? ' on' : ''}" style="--d:${done.toFixed(3)}"></span>`
    + `<span class="ck-cap">${esc(g.label)}</span>`
    + (g.blocked > 0 ? `<span class="ck-cond bad"><b>${g.blocked}</b><i>${esc(S.guard_in_reach)}</i></span>` : '')
    + `<b class="ck-vf">${g.wait > 0 ? esc(waitCount(g.wait)) : esc(S.ready)}</b></div>`;
}

/** The vitals strip: one row of up to four gauges. */
function vitalsHtml(list: readonly Vital[] | undefined): string {
  if (!list || list.length === 0) return '';
  return `<div class="ck-vitals n${list.length}">${list.map(vitalHtml).join('')}</div>`;
}

/** The fact rows. `cls` adds a class: `ck-f2` sets them in two columns. */
export function factsHtml(list: readonly Fact[] | undefined, cls = ''): string {
  if (!list || list.length === 0) return '';
  return `<div class="ck-facts${cls ? ` ${cls}` : ''}">` + list.map((f) =>
    `<div class="ck-fact"${f.title ? ` title="${esc(f.title)}"` : ''}>`
    + `<span class="ck-cap">${esc(f.key)}</span><span class="ck-fv">${f.val}</span></div>`).join('')
    + '</div>';
}

/** The effect block of a catalogue item: NOW → AFTER and the change. */
export function effectHtml(rows: readonly EffectRow[] | undefined): string {
  if (!rows || rows.length === 0) return '';
  return `<div class="ck-eff"><span class="ck-eh"></span><span class="ck-eh ck-cap">${S.effect_now}</span>`
    + `<span class="ck-eh"></span><span class="ck-eh ck-cap">${S.effect_after}</span><span class="ck-eh"></span>`
    + rows.map((r) => `<span class="ck-ek ck-cap">${esc(r.key)}</span><span class="ck-en">${r.now}</span>`
      + '<span class="ck-ea">→</span>'
      + `<span class="ck-ef">${r.after}</span><span class="ck-ed${r.tone ? ` ${r.tone}` : ''}">${r.delta ?? ''}</span>`)
      .join('')
    + '</div>';
}

/**
 * The head: the mark, the title in its ink, the kind, a chip and the ✕.
 * With `statusOnKind` the chip ends the kind line, under the title
 * (R1879).
 */
export function headHtml(spec: CardSpec): string {
  const chip = spec.status ? chipHtml(spec.status.text, spec.status.tone) : '';
  const kind = spec.kindHtml ? `<i>${spec.kindHtml}</i>` : spec.kind ? `<i>${esc(spec.kind)}</i>` : '';
  const below = spec.statusOnKind === true && chip !== '';
  return '<div class="ck-head">'
    + (spec.mark ? `<span class="ck-mk">${spec.mark}</span>` : '')
    + `<span class="ck-ht"><span class="ck-tl"><b${inkAttr(spec.ink)}>${esc(spec.title)}</b>`
    + `${spec.titleHtml ?? ''}</span>`
    + (below ? `<span class="ck-kl">${kind}${chip}</span>` : kind)
    + (spec.pos ? `<i class="ck-pos">${esc(spec.pos)}</i>` : '') + '</span>'
    + (below ? '' : chip)
    + (spec.size !== 'peek' ? spec.headExtra ?? '' : '')
    + (spec.close && spec.size !== 'peek' ? closeHtml(spec.closeAct) : '')
    + '</div>';
}

/** How many presses ⋯ holds, in every section; a choice counts as one. */
function moreCount(more: NonNullable<CardSpec['more']>): number {
  return more.items.length + (more.sections ?? []).reduce((n, s) => n + s.items.length + (s.pick ? 1 : 0), 0);
}

/** True when a ⋯ holds a press, so the card draws ⋯. */
export function hasMore(more: NonNullable<CardSpec['more']>): boolean {
  return moreCount(more) > 0;
}

/** ⋯: the last press of the action row, at its right edge. It stands in
 *  the foot of a card and in the row of a closed context sheet (R2008). */
export function morePressHtml(more: NonNullable<CardSpec['more']>): string {
  return `<button class="ck-b qt ck-more${more.open ? ' on' : ''}" data-act="more"`
    + ` title="${S.more_title}" aria-label="${S.more_title}"`
    + ` aria-expanded="${more.open}">${S.more_mark}</button>`;
}

/** True when a section of the EDIT grid holds a press or a pick. */
const editShows = (s: MoreSection): boolean => s.items.length > 0 || !!s.pick;

/** True when EDIT holds a press, so the head draws EDIT. */
function hasEdit(edit: CardSpec['edit']): edit is NonNullable<CardSpec['edit']> {
  return edit !== undefined && edit.sections.some(editShows);
}

/** EDIT (R2008): a quiet square press in the action row, left of ⋯, with
 *  the pencil in the 12 px box of the sheet's ✕. */
export function editPressHtml(edit: CardSpec['edit']): string {
  if (!hasEdit(edit)) return '';
  return `<button class="ck-b qt ck-edit${edit.open ? ' on' : ''}" data-act="edit"`
    + ` title="${esc(edit.title)}" aria-label="${esc(edit.title)}"`
    + ` aria-expanded="${edit.open}">${editGlyph()}</button>`;
}

/** One section head of the ⋯ grid: the cap word over the width of the
 *  grid, with a rule over it after the first section. */
const moreHead = (head: string): string => `<div class="ck-gh"><span class="ck-cap">${esc(head)}</span></div>`;

/**
 * The ⋯ grid (R1845): the presses of `items`, then each section under its
 * head. A section head spans the grid, so the presses of a section start
 * on a new row of three. A grid of one section and no head is the bare
 * grid of before. A section may hold a choice that spans the grid, as a
 * section of the EDIT grid does: the trigger of a signature order (R2376).
 */
function moreGridHtml(more: NonNullable<CardSpec['more']>, env: CardEnv): string {
  if (!hasMore(more)) return '';
  const sections = (more.sections ?? []).filter(editShows);
  return '<div class="ck-grid">'
    + (more.items.length > 0 ? (more.head && sections.length > 0 ? moreHead(more.head) : '') + pressesOf(more.items, env) : '')
    + sectionsHtml(sections, env)
    + '</div>';
}

/** The EDIT grid (R2008): each section under its head, in the look of
 *  the ⋯ grid and in its place. The class `ck-egrid` names it. */
function editGridHtml(edit: NonNullable<CardSpec['edit']>, env: CardEnv): string {
  return `<div class="ck-grid ck-egrid">${sectionsHtml(edit.sections.filter(editShows), env)}</div>`;
}

/** The presses of a grid, in order. */
function pressesOf(list: readonly Action[], env: CardEnv): string {
  return list.map((a) => buttonHtml(a, env)).join('');
}

/** Each section of a grid under its head: its choice, then its presses. */
function sectionsHtml(sections: readonly MoreSection[], env: CardEnv): string {
  return sections.map((s) => moreHead(s.head) + (s.pick ? `<div class="ck-gp">${s.pick}</div>` : '')
    + pressesOf(s.items, env)).join('');
}

/**
 * The note block of a card, or '' for a note with no line. A peek drops
 * the note of a spec with facts (`peekOf`), so a peek that keeps a note
 * beside its facts draws this block as its `peekBody`: the effect line of
 * an arcade structure (R2424).
 */
export function noteHtml(note: CardSpec['note']): string {
  return note && note.lines.length > 0
    ? `<div class="ck-note${note.tone ? ` ${note.tone}` : ''}${note.foot ? ' foot' : ''}">`
      + note.lines.map((l) => `<p>${l}</p>`).join('') + '</div>'
    : '';
}

/**
 * One card, in its size. The peek size trims the spec through `peekOf`, so
 * a caller may hand the full card spec to the peek host.
 */
export function cardHtml(input: CardSpec, env: CardEnv): string {
  const spec = input.size === 'peek' ? peekOf(input) : input;
  const peek = spec.size === 'peek';
  const acts = spec.actions ?? [];
  const more = peek ? undefined : spec.more;
  const note = noteHtml(spec.note);
  const edit = peek ? undefined : spec.edit;
  const withEdit = hasEdit(edit);
  // The ⋯ grid and the EDIT grid exclude each other; the owner of the
  // state opens one at a time, and ⋯ wins a spec that names both.
  const grid = more?.open ? moreGridHtml(more, env)
    : withEdit && edit.open ? editGridHtml(edit, env) : '';
  const withMore = more !== undefined && hasMore(more);
  const foot = acts.length > 0 || withMore || withEdit
    ? '<div class="ck-acts">' + pressesOf(acts, env)
      + editPressHtml(edit)
      + (withMore ? morePressHtml(more) : '')
      + '</div>'
    : '';
  const edge = edgeAttr(spec);
  return `<div class="ck ck-sz-${spec.size}${peek ? ' ck-peekg' : ''}${edge ? ' ck-own' : ''}"${edge}>`
    + headHtml(spec)
    + (peek ? '' : armHtml(spec.armed, env))
    + vitalsHtml(spec.vitals)
    + guardChipHtml(spec.guard)
    + (peek ? '' : condsHtml(spec.conds))
    // An open ⋯ or EDIT grid takes the place of the facts, so the card
    // does not grow past the sheet and scroll.
    + (grid ? '' : factsHtml(spec.facts))
    + (peek ? spec.peekBody ?? '' : spec.body ?? '')
    + (peek ? '' : effectHtml(spec.effect))
    + (spec.note?.foot ? '' : note)
    + grid
    + foot
    + (spec.note?.foot ? note : '')
    + '</div>';
}
